import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router';
import { Row, Col, Label, Button } from 'reactstrap';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@jem-open/jem-ui';
import axios from 'axios';
import moment from 'moment';
import { environment } from '../../../environment/environment';
import { useEnabledFeatureFlag } from '../../../utils/feature-flagging/FeatureFlagProvider';
import FeatureFlags from '../../../utils/feature-flagging/FeatureFlags';
import EmployerReconCalendar from './EmployerReconCalendar';
import CreateReconEventModal from './CreateEvent';
import EmployerHRCRecons from './EmployerHRCRecons';
import MultiProductReconTab from './MultiProductRecon/MultiProductReconTab';

const RECON_PAGE_TABS = {
  HRC: 'hrc',
  EWA: 'ewa',
  MULTI_PRODUCT: 'multiProduct',
};

const EmployerReconsPage = (props) => {
  const isManageReconsEnabled = useEnabledFeatureFlag(
    FeatureFlags.ADMIN_PORTAL_AUTOMATED_ODP_INVOICING
  );
  const isMultiProductReconEnabled = useEnabledFeatureFlag(
    FeatureFlags.ADMIN_PORTAL_MULTI_PRODUCT_RECON
  );

  const location = useLocation();

  const employerId = location.pathname.split('/')[3];

  const [employerDetails, setEmployerDetails] = React.useState(() =>
    location.state?.save_data
      ? {
          payCycles: location.state.save_data.pay_cycles,
          branches: location.state.save_data.branches,
          employerName: location.state.save_data.company_name,
        }
      : null,
  );

  React.useEffect(() => {
    if (employerDetails) return;
    axios
      .get(`${environment.baseUrl}/employers/${employerId}/`)
      .then((res) => {
        setEmployerDetails({
          payCycles: res.data.pay_cycles,
          branches: res.data.branches,
          employerName: res.data.company_name,
        });
      })
      .catch((error) => {
        console.error('Failed to load employer details', error);
      });
  }, [employerId, employerDetails]);

  const [calendarEvents, setCalendarEvents] = React.useState([]);

  const [loading, setLoading] = React.useState(false);

  const [isCreateEvent, setIsCreateEvent] = React.useState(false);

  const [currentActiveTab, setCurrentActiveTab] = useState(RECON_PAGE_TABS.HRC);

  const handleOnCloseModal = (reload) => {
    if (reload) {
      checkForRecons();
    }
  };

  const startTime = moment().startOf('month').format('YYYY-MM-DD');
  const endTime = moment()
      .startOf('month')
      .add(12, 'month')
      .format('YYYY-MM-DD');

  const checkForRecons = useCallback(async () => {
    setLoading(true);

    axios
      .get(`/api/calendar_events/instances?employerId=${employerId}&startTime=${startTime}&endTime=${endTime}`)
      .then((response) => {
        setCalendarEvents(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }, [employerId]);

  React.useEffect(() => {
    checkForRecons();
  }, [checkForRecons]);

  if (loading || !employerDetails) {
    return (
      <Row className="my-3">
        <Col className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {isCreateEvent && (
        <CreateReconEventModal
          isOpen={isCreateEvent}
          setIsCreateEvent={setIsCreateEvent}
          payCycles={employerDetails.payCycles}
          branches={employerDetails.branches}
          employerName={employerDetails.employerName}
          calendarEvents={calendarEvents}
          onClose={handleOnCloseModal}
        />
      )}

      <div className="bg-primary">
        <Row className="p-4">
          <Col className="col-10">
            <Label className="h2 text-white">Employer Recons</Label>
          </Col>
          {isManageReconsEnabled && currentActiveTab === RECON_PAGE_TABS.EWA && (
            <Col>
              <Button onClick={() => setIsCreateEvent(true)}>
                Create Event
              </Button>
            </Col>
          )}
        </Row>
      </div>

      <Tabs
        value={currentActiveTab}
        onValueChange={setCurrentActiveTab}
        className="px-4 pt-4"
      >
        <TabsList>
          <TabsTrigger value={RECON_PAGE_TABS.HRC}>HRC</TabsTrigger>
          <TabsTrigger value={RECON_PAGE_TABS.EWA}>EWA</TabsTrigger>
          {isMultiProductReconEnabled && (
            <TabsTrigger value={RECON_PAGE_TABS.MULTI_PRODUCT}>
              Multi-Product Recon
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value={RECON_PAGE_TABS.HRC} className="mt-0 p-4">
          <EmployerHRCRecons employerId={employerId} />
        </TabsContent>

        <TabsContent value={RECON_PAGE_TABS.EWA} className="mt-0 p-2">
          <EmployerReconCalendar employerId={employerId} />
        </TabsContent>

        {isMultiProductReconEnabled && (
          <TabsContent
            value={RECON_PAGE_TABS.MULTI_PRODUCT}
            className="mt-0 p-2"
          >
            <MultiProductReconTab
              employerId={employerId}
              employerName={employerDetails.employerName}
              payCycles={employerDetails.payCycles}
            />
          </TabsContent>
        )}
      </Tabs>
    </>
  );
};

export default EmployerReconsPage;
